import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://www.noleggioincloud.app";

/** Returns true when page has been scrolled past 30px */
export function useScrolled() {
  const [s, setS] = useState(false);
  useEffect(() => {
    const h = () => setS(window.scrollY > 30);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  return s;
}

/**
 * Activates IntersectionObserver on every `.an` element in the DOM.
 */
export function useAnim() {
  useEffect(() => {
    const els = document.querySelectorAll(".an");
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); }
      }),
      { threshold: 0.1 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/**
 * Full SEO hook — sets title, description, canonical, OG, Twitter, JSON-LD.
 *
 * @param {Object} opts
 * @param {string}  opts.title
 * @param {string}  opts.description
 * @param {string}  [opts.path]      — override URL path (default: current pathname)
 * @param {Object}  [opts.schema]    — JSON-LD object for this page
 * @param {boolean} [opts.noindex]   — pagina da non indicizzare (404, pagine
 *   "sottili"): aggiunge <meta name="robots" content="noindex,follow"> e toglie
 *   canonical/og:url, che altrimenti punterebbero a un URL che non esiste.
 */
export function useSEO({ title, description, path, schema, noindex } = {}) {
  const { pathname } = useLocation();
  const canonicalPath = path ?? pathname;
  const canonical = `${SITE_URL}${canonicalPath === "/" ? "" : canonicalPath}`;

  useEffect(() => {
    const setMeta = (sel, attr, attrVal, contentVal) => {
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute("content", contentVal);
    };

    // ── Title ──────────────────────────────────────────────────────────────
    if (title) {
      document.title = title;
      setMeta('meta[property="og:title"]',  "property", "og:title",         title);
      setMeta('meta[name="twitter:title"]', "name",     "twitter:title",    title);
    }

    // ── Description ────────────────────────────────────────────────────────
    if (description) {
      setMeta('meta[name="description"]',          "name",     "description",          description);
      setMeta('meta[property="og:description"]',   "property", "og:description",       description);
      setMeta('meta[name="twitter:description"]',  "name",     "twitter:description",  description);
    }

    // ── Robots + canonical ─────────────────────────────────────────────────
    // Il meta robots lo creiamo solo per le pagine noindex e lo togliamo alla
    // pagina successiva (marcato data-nic per non toccare tag di altri).
    const robotsSel = 'meta[name="robots"][data-nic]';
    let robots = document.querySelector(robotsSel);
    let link = document.querySelector('link[rel="canonical"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        robots.setAttribute("data-nic", "1");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex,follow");
      if (link) link.remove();
      const og = document.querySelector('meta[property="og:url"]');
      if (og) og.remove();
    } else {
      if (robots) robots.remove();
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      setMeta('meta[property="og:url"]', "property", "og:url", canonical);
    }

    // ── JSON-LD per-page ───────────────────────────────────────────────────
    const LD_ID = "ld-page";
    let ldEl = document.getElementById(LD_ID);
    if (schema) {
      if (!ldEl) {
        ldEl = document.createElement("script");
        ldEl.id   = LD_ID;
        ldEl.type = "application/ld+json";
        document.head.appendChild(ldEl);
      }
      ldEl.textContent = JSON.stringify(schema);
    } else if (ldEl) {
      ldEl.remove();
    }
  }, [title, description, canonical, schema, noindex]);
}
